(function () {
  const SESSION_STORAGE_KEY = 'boostSDSessionId';
  const SERVICE_NAMES = ['Boost AI Search & Discovery'];
  const SERVICE_IDS = ['mWxyUoi5kiWELk'];
  const CONSENT_SYNC_RETRY_MS = 100;
  const CONSENT_SYNC_MAX_ATTEMPTS = 50;
  const LEGACY_ANALYTICS_STORAGE_KEYS = [
    'uc-boost-analytics-consent',
    'boostSdSessionId',
    'boostPFSAnalytics',
    'boostPFSSessionId',
    'boostPFSRequestIds',
    'boostPFSClickedProduct',
  ];

  let consentGranted = false;
  let consentSyncAttempts = 0;

  function readStorage(key) {
    try {
      return localStorage.getItem(key);
    } catch {
      return null;
    }
  }

  function writeStorage(key, value) {
    try {
      localStorage.setItem(key, value);
    } catch {
      // Storage may be unavailable in restricted browser contexts.
    }
  }

  function removeStorage(key) {
    try {
      localStorage.removeItem(key);
    } catch {
      // Storage may be unavailable in restricted browser contexts.
    }
  }

  function generateUUID() {
    if (window.crypto?.randomUUID) {
      return window.crypto.randomUUID();
    }

    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (character) => {
      const random = Math.floor(Math.random() * 16);
      const value = character === 'x' ? random : (random & 0x3) | 0x8;
      return value.toString(16);
    });
  }

  function clearAnalyticsStorage() {
    removeStorage(SESSION_STORAGE_KEY);
    LEGACY_ANALYTICS_STORAGE_KEYS.forEach(removeStorage);
  }

  function setConsent(granted) {
    consentGranted = granted === true;

    if (!consentGranted) {
      clearAnalyticsStorage();
    }

    window.dispatchEvent(
      new CustomEvent('boost:analytics-consent', {
        detail: { granted: consentGranted },
      })
    );
  }

  function getConsentFromUcEvent(detail) {
    if (!detail || detail.event !== 'consent_status') return null;

    for (const serviceName of SERVICE_NAMES) {
      if (Object.prototype.hasOwnProperty.call(detail, serviceName)) {
        return detail[serviceName] === true;
      }
    }

    return null;
  }

  function getConsentFromUcConsent(detail) {
    return getConsentFromServices(Object.values(detail?.services || {}));
  }

  function getConsentFromServices(services) {
    const serviceList = Array.isArray(services) ? services : Object.values(services || {});
    const service = serviceList.find(
      (item) => SERVICE_NAMES.includes(item?.name) || SERVICE_IDS.includes(item?.id)
    );
    if (!service) return null;

    if (typeof service.consent?.given === 'boolean') {
      return service.consent.given;
    }

    if (typeof service.consent?.status === 'boolean') {
      return service.consent.status;
    }

    return null;
  }

  async function syncConsentFromUsercentrics() {
    if (typeof window.UC_UI?.getServicesBaseInfo !== 'function') return false;

    try {
      const services = await window.UC_UI.getServicesBaseInfo();
      const granted = getConsentFromServices(services);
      if (granted === null) return false;

      setConsent(granted);
      return true;
    } catch {
      // Usercentrics may still be initializing. UC_UI_INITIALIZED will retry.
      return false;
    }
  }

  function scheduleConsentSync() {
    syncConsentFromUsercentrics().then((synced) => {
      if (synced || consentSyncAttempts >= CONSENT_SYNC_MAX_ATTEMPTS) return;

      consentSyncAttempts += 1;
      window.setTimeout(scheduleConsentSync, CONSENT_SYNC_RETRY_MS);
    });
  }

  function getSessionId() {
    if (!consentGranted) return '';

    let sessionId = readStorage(SESSION_STORAGE_KEY);
    if (!sessionId) {
      sessionId = generateUUID();
      writeStorage(SESSION_STORAGE_KEY, sessionId);
    }
    return sessionId;
  }

  function appendEventType(params, eventType) {
    params.delete('event_type');
    if (consentGranted && eventType) {
      params.set('event_type', eventType);
    }
    return params;
  }

  function getPageType() {
    return window.boostPFSConfig?.general?.template || '';
  }

  function trackProduct({ productId, requestId, queryString, action, userAction = 'view_product', variantId } = {}) {
    if (!consentGranted || productId == null || productId === '') return false;

    const config = window.boostPFSConfig || {};
    const analyticsUrl = config.api?.analyticsUrl;
    const tenantId = config.shop?.domain;
    const sessionId = getSessionId();

    if (!analyticsUrl || !tenantId || !sessionId) return false;

    const payload = {
      tid: tenantId,
      eid: generateUUID(),
      pid: String(productId),
      t: new Date().toISOString(),
      u: userAction,
      a: action,
      r: document.referrer || '',
      sid: sessionId,
    };

    if (queryString) payload.qs = queryString;
    if (requestId) payload.rid = requestId;
    if (variantId != null && variantId !== '') payload.vid = String(variantId);

    const pageType = getPageType();
    if (pageType) payload.pg = pageType;

    fetch(analyticsUrl, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json;charset=UTF-8' },
      body: JSON.stringify(payload),
      keepalive: true,
      credentials: 'omit',
    }).catch(() => {
      // Analytics must never interfere with navigation or functional search.
    });

    return true;
  }

  window.addEventListener('ucEvent', (event) => {
    const granted = getConsentFromUcEvent(event.detail);
    if (granted !== null) setConsent(granted);
  });

  window.addEventListener('UC_CONSENT', (event) => {
    const granted = getConsentFromUcConsent(event.detail);
    if (granted !== null) setConsent(granted);
  });

  window.addEventListener('UC_UI_INITIALIZED', scheduleConsentSync);

  scheduleConsentSync();

  LEGACY_ANALYTICS_STORAGE_KEYS.forEach(removeStorage);

  window.boostAnalytics = {
    appendEventType,
    isAllowed: () => consentGranted,
    trackProduct,
  };
})();
